import { ListRange } from '@angular/cdk/collections';
import {
  CdkVirtualScrollViewport,
  VIRTUAL_SCROLL_STRATEGY,
  VirtualScrollStrategy,
} from '@angular/cdk/scrolling';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

type ItemHeight = number[];

export class SidePanelAccountsScrollerStrategy implements VirtualScrollStrategy {
  private viewport?: CdkVirtualScrollViewport;
  private scrolledIndexChange$ = new Subject<number>();
  private cumulativeOffsets: number[] = [];

  public scrolledIndexChange: Observable<number> =
    this.scrolledIndexChange$.pipe(distinctUntilChanged());

  // Buffer optimization for Firefox
  _minBufferPx = 1000;
  _maxBufferPx = 2000;

  constructor(private itemHeights: ItemHeight) {
    this.updateCumulativeOffsets();
  }

  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;
    this.updateTotalContentSize();
    this.updateRenderedRange();
  }

  detach(): void {
    this.scrolledIndexChange$.complete();
    delete this.viewport;
  }

  public updateItemHeights(itemHeights: ItemHeight): void {
    this.itemHeights = itemHeights;
    this.updateCumulativeOffsets();
    this.updateTotalContentSize();
    this.updateRenderedRange();
  }

  private updateCumulativeOffsets(): void {
    let offset = 0;
    this.cumulativeOffsets = this.itemHeights.map((height) => {
      const current = offset;
      offset += height;
      return current;
    });
  }

  private findFirstVisibleIndex(scrollOffset: number): number {
    let low = 0;
    let high = this.cumulativeOffsets.length - 1;
    let result = 0;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (this.cumulativeOffsets[mid] <= scrollOffset) {
        result = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    return result;
  }

  private updateRenderedRange(): void {
    if (!this.viewport || this.itemHeights.length === 0) {
      return;
    }

    const viewportSize = this.viewport.getViewportSize();
    const scrollOffset = this.viewport.measureScrollOffset();
    const firstVisibleIndex = this.findFirstVisibleIndex(scrollOffset);

    const BUFFER_BEFORE = viewportSize > 50 ? viewportSize - 50 : viewportSize;
    const BUFFER_AFTER = BUFFER_BEFORE + 50;

    const start = Math.max(0, firstVisibleIndex);
    const end = Math.min(this.itemHeights.length, firstVisibleIndex + BUFFER_AFTER);

    const newRange = { start, end };
    const oldRange = this.viewport.getRenderedRange();

    if (newRange.start === oldRange.start && newRange.end === oldRange.end) {
      return;
    }

    this.viewport.setRenderedRange(newRange);
    this.viewport.setRenderedContentOffset(this.cumulativeOffsets[newRange.start] || 0);
    this.scrolledIndexChange$.next(newRange.start);
  }

  private updateTotalContentSize(): void {
    if (!this.viewport) {
      return;
    }
    const totalSize =
      this.cumulativeOffsets.length > 0
        ? this.cumulativeOffsets[this.cumulativeOffsets.length - 1] +
          this.itemHeights[this.itemHeights.length - 1]
        : 0;
    this.viewport.setTotalContentSize(totalSize);
  }

  onContentScrolled(): void {
    this.updateRenderedRange();
  }
  onDataLengthChanged(): void {
    this.updateCumulativeOffsets();
    this.updateTotalContentSize();
    this.updateRenderedRange();
  }
  onContentRendered(): void {}
  onRenderedOffsetChanged(): void {}
  scrollToIndex(index: number, behaviour: ScrollBehavior): void {
    if (this.viewport) {
      this.viewport.scrollToOffset(this.cumulativeOffsets[index] || 0, behaviour);
    }
  }
}

@Directive({
  selector: 'cdk-virtual-scroll-viewport[sidePanelAccountsScrollerStrategyDirective]',
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: (dir: any) => dir.scrollStrategy,
      deps: [forwardRef(() => SidePanelAccountsScrollerStrategyDirective)],
    },
  ],
  standalone: false,
})
export class SidePanelAccountsScrollerStrategyDirective implements OnChanges {
  @Input() itemHeights: ItemHeight = [];
  scrollStrategy = new SidePanelAccountsScrollerStrategy(this.itemHeights);
  constructor(private cd: ChangeDetectorRef) {}
  ngOnChanges(changes: SimpleChanges) {
    if ('itemHeights' in changes) {
      this.scrollStrategy.updateItemHeights(this.itemHeights);
      this.cd.detectChanges(); // Immediate sync
    }
  }
}
